<template>
  <div>
    <div class="container-fluid navigation-bar px-1">
      <div class="navigation-location">
        <button @click="toggle" class="burger">
          <font-awesome-icon icon="fa-solid fa-bars"/>
        </button>
        <span class="d-flex"> </span>
      </div>
      <div class="onlice-check mx-2 d-none d-md-block">
        <OnlineChecker/>
      </div>
      <div class="user-settings d-flex" style="align-items:center; gap: 8px;">
        <!-- Mobile quick actions dropdown: Online, Theme, Refresh -->
        <div class="dropdown d-inline-flex d-md-none">
          <button
            class="burger dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            aria-haspopup="true"
            aria-label="Open quick actions"
            title="Quick actions"
          >
            <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
          </button>
          <div class="dropdown-menu dropdown-menu-end p-2" @click.stop>
            <div class="dropdown-item-text px-2 py-1">
              <OnlineChecker/>
            </div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-item px-2 py-1">
              <ThemeToggle />
            </div>
            <button class="dropdown-item d-flex align-items-center gap-2" @click="refresh">
              <font-awesome-icon icon="fa-solid fa-arrows-rotate"/>
              <span>Refresh</span>
            </button>
          </div>
        </div>

<!--        <div v-if="showFlashMessage" class="custom-tooltip-right">-->
<!--          <div class="tooltip-arrow"></div>-->
<!--          <div class="tooltip-body">-->
<!--            click here to select specific store reports-->
<!--          </div>-->
<!--        </div>-->

        <span class="d-none d-md-inline-flex"><ThemeToggle /></span>

        <!-- Searchable Store Picker: only show dropdown if there is more than one real store -->
        <!-- Notes:
             - Removed Bootstrap 'dropend' and 'dropdown-menu-end' to ensure the menu opens from the left side.
             - Using plain 'dropdown' so the menu aligns to the left edge of the toggle by default.
        -->
        <div class="dropdown store-picker" v-if="showStoreDropdown">
          <button
              ref="storeToggle"
              class="select-doc-btn online-status dropdown-toggle store-toggle-btn store-toggle-ghost"
              type="button"
              data-bs-toggle="dropdown"
              data-bs-offset="0,6"
              data-bs-boundary="viewport"
              data-bs-display="static"
              aria-expanded="false"
              aria-haspopup="listbox"
              :aria-label="'Change store. Current: ' + ((store && store.StoreName) ? store.StoreName : 'None')"
          >
            <span class="store-toggle-row">
              <font-awesome-icon icon="fa-solid fa-store" class="store-icon"/>
              <span class="store-toggle-name">
                {{ (store && store.StoreName) ? store.StoreName : 'Select Store' }}
              </span>
              <span v-if="store && store.mainBranch" class="chip-main" aria-label="Main store">Main</span>
            </span>
          </button>

          <div class="dropdown-menu p-2" @click.stop>
            <!-- Search input: do not close dropdown on input interactions -->
            <div class="search-row">
              <input
                ref="storeSearch"
                v-model.trim="searchQuery"
                type="text"
                class="form-control store-search"
                placeholder="Search stores…"
                role="combobox"
                aria-expanded="true"
                :aria-controls="listboxId"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.enter.prevent="selectActive()"
                @keydown.esc.prevent="closeDropdown()"
              />
              <button v-if="searchQuery" class="btn-clear" @click="clearSearch" aria-label="Clear search">×</button>
            </div>

            <!-- Recent selections -->
            <ul v-if="recentStores.length" class="list-unstyled mt-2 mb-2 recent-list">
              <li class="section-label">Recent</li>
              <li v-for="(s, idx) in recentStores" :key="'r-' + (s.StoreDB || s.StoreName)"
                  :class="['dropdown-item store-item', isActiveIndex(idx, 'recent') ? 'active' : '']"
                  role="option"
                  :aria-label="(s.StoreName || '') + (s.mainBranch ? ' (Main store)' : '')"
                  @mousemove="hoverIndex = { scope: 'recent', index: idx }"
                  @click="selectStoreAndRemember(s)">
                <span class="store-row">
                  <span class="store-name">{{ s.StoreName }}</span>
                  <span v-if="s.mainBranch" class="badge-main" aria-hidden="false">Main</span>
                </span>
              </li>
              <li class="divider" role="separator"></li>
            </ul>

            <!-- Filtered results -->
            <ul class="list-unstyled mb-0" :id="listboxId" role="listbox">
              <li v-if="!filteredStores.length" class="dropdown-item disabled">
                No stores match “{{ searchQuery }}”.
              </li>
              <li v-for="(s, idx) in filteredStores"
                  :key="'s-' + (s.StoreDB || s.StoreName)"
                  :class="['dropdown-item store-item', isActiveIndex(idx, 'main') ? 'active' : '']"
                  role="option"
                  :aria-selected="isActiveIndex(idx, 'main') ? 'true' : 'false'"
                  :aria-label="(s.StoreName || '') + (s.mainBranch ? ' (Main store)' : '')"
                  @mousemove="hoverIndex = { scope: 'main', index: idx }"
                  @click="selectStoreAndRemember(s)">
                <span class="store-row">
                  <span class="store-name">{{ s.StoreName }}</span>
                  <span v-if="s.mainBranch" class="badge-main" aria-hidden="false">Main</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <router-link
          :to="{ name: 'help' }"
          class="burger d-none d-md-inline-flex"
          aria-label="User Manual"
          title="User Manual"
          style="text-decoration: none; display: inline-flex; align-items: center; justify-content: center;"
        >
          <font-awesome-icon icon="fa-solid fa-circle-question"/>
        </router-link>
        <button class="burger d-none d-md-inline-flex" @click="refresh" aria-label="Refresh">
          <font-awesome-icon icon="fa-solid fa-arrows-rotate"/>
        </button>
        <PwaInstallButton />
      </div>
    </div>
  </div>
</template>

<script>
import OnlineChecker from "@/utils/OnlineChecker.vue";
import PwaInstallButton from "@/components/layout/utils/PwaInstallButton.vue";
import ThemeToggle from "@/components/layout/ThemeToggle.vue";
import { PdfService } from "@/Services/pdf/PdfService";

export default {
  name: "NavigationBar",
  components: {
    OnlineChecker,
    PwaInstallButton,
    ThemeToggle,
  },
  created() {
    try {
      const stores = JSON.parse(localStorage.getItem("stores"));
      if (stores) {
        this.$store.commit("setStores", stores);
      }
    } catch (error) {
      console.error("Error loading stores from localStorage:", error);
    }

    try {
      const selectedStore = JSON.parse(localStorage.getItem("selected_store"));
      if (selectedStore) {
        this.$store.commit("setStore", selectedStore);
        if (selectedStore.StoreName !== 'All') {
          this.loadStoreData();
        } else {
          this.resetStoresData();
        }
      }
    } catch (error) {
      console.error("Error loading selected store:", error);
    }

    // Load recent stores for quick access (localStorage only)
    try {
      const recentRaw = localStorage.getItem("recent_stores");
      const parsed = JSON.parse(recentRaw);
      this.recentStores = Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      this.recentStores = [];
    }
  },

  data() {
    return {
      showFlashMessage: true,
      // Searchable store selector local state
      searchQuery: "",
      hoverIndex: { scope: "main", index: 0 },
      listboxId: "store-listbox",
      recentStores: [],
      maxRecent: 5,
    };
  },
  mounted() {
    setTimeout(() => {
      this.showFlashMessage = false;
    }, 7000);
    // Auto-focus the search input when the dropdown opens
    document.addEventListener("shown.bs.dropdown", (e) => {
      if (this.$refs.storeToggle && e.target === this.$refs.storeToggle) {
        setTimeout(() => this.focusSearch(), 50);
      }
    });
  },
  computed: {
    stores() {
      let stores = this.$store.getters.getStores;
      if (stores.length > 0 && !stores.find(s=>s.StoreName === 'All')) {
        stores.unshift({
          StoreName: `All`,
          StoreDB: null,
          Group: stores[0].Group,
        });
      }
      return stores;
    },
    store() {
      return this.$store.getters.getStore;
    },
    /**
     * Real stores excluding the synthetic “All” entry.
     * @returns {Array<{StoreName:string, StoreDB:(string|null), Group?:any}>}
     */
    realStores() {
      const list = this.$store.getters.getStores || [];
      return list.filter(s => s && s.StoreName !== 'All');
    },
    /**
     * Show dropdown only if there are 2 or more real stores.
     */
    showStoreDropdown() {
      return this.realStores.length > 1;
    },
    /**
     * Filtered stores per search query, keeping “All” at the top if present.
     */
    filteredStores() {
      const q = (this.searchQuery || '').toLowerCase();
      const all = this.stores.find(s => s.StoreName === 'All');
      const rest = this.stores.filter(s => s.StoreName !== 'All');
      const filtered = !q ? rest : rest.filter(s => (s.StoreName || '').toLowerCase().includes(q));
      return all ? [all, ...filtered] : filtered;
    },
  },
  props: {
    toggle: {
      type: Function,
      require: true,
    },
  },
  watch: {
    /**
     * When the stores list changes, auto‑select the sole store (if applicable).
     */
    realStores: {
      immediate: true,
      handler(newList) {
        this.ensureSingleStoreAutoSelect(newList);
      }
    }
  },
  methods: {
    /**
     * Quick demo handler: generate a small PDF using pdfmake to verify integration.
     * Uses PdfService (loads from CDN on first use). No API calls; runs fully client-side.
     */
    async generateTestPdf() {
      try {
        const iconUrl = `${window.location.origin}/img/icons/android-chrome-192x192.png`;
        await PdfService.generateSampleReport({
          title: 'Lyt‑ning Sample PDF',
          company: { name: 'Lyt‑ning Analytics', logoUrl: iconUrl },
          items: [
            { name: 'Demo Line Item A', qty: 2, unitPrice: 19.99 },
            { name: 'Demo Line Item B', qty: 1, unitPrice: 49.0 },
            { name: 'Demo Service C', qty: 3, unitPrice: 15.0 }
          ],
          images: [
            { url: iconUrl, caption: 'PWA App Icon' }
          ]
        }, 'open');
      } catch (err) {
        console.error('PDF generation failed:', err);
        this.$utils && this.$utils.toast && this.$utils.toast('Unable to generate PDF. Please check your internet connection.', 'error');
      }
    },
    /** Set the selected store in Vuex and trigger existing data loads. */
    async selectStore(store) {
      this.$store.commit("setStore", store);
      // When "All" (StoreDB === null) is selected, avoid store-specific data loads
      if (store && (store.StoreName === 'All' || store.StoreDB === null)) {
        await this.resetStoresData();
      } else {
        await this.loadStoreData();
      }
    },
    /** Reset store-dependent data to defaults when "All" is selected. */
    resetStoresData() {
      this.$utils.showLoading();
      return Promise.all([
        this.$store.commit(`setStoreCurrencies`, []),
        this.$store.commit(`setBaseCurrency`, {Symbol: "K", rate: 1, Name: "Kwacha", id: 1})
      ]).finally(()=>this.$utils.hideLoading());
    },
    /** Load store-dependent datasets via Vuex (no direct API calls here). */
    loadStoreData() {
      this.$utils.showLoading();
      return Promise.all([
        this.$store.dispatch(`getAllCurrencies`),
        this.$store.dispatch(`getBaseCurrency`),
        this.$store.dispatch("getAllUsers")
      ]).finally(()=>this.$utils.hideLoading());
    },
    /** Force a full reload (kept from existing behavior). */
    async refresh() {
      window.location.reload(true);
    },

    /** Focus the search input inside the store dropdown. */
    focusSearch() {
      this.$nextTick(() => this.$refs.storeSearch && this.$refs.storeSearch.focus());
    },
    /** Clear the search term and reset active index. */
    clearSearch() {
      this.searchQuery = "";
      this.hoverIndex = { scope: "main", index: 0 };
      this.focusSearch();
    },
    /**
     * Move the active (highlighted) list item by delta positions.
     * @param {number} delta Positive or negative step count.
     */
    moveActive(delta) {
      const scope = this.hoverIndex.scope;
      const list = scope === "recent" ? this.recentStores : this.filteredStores;
      if (!list.length) return;
      let next = (this.hoverIndex.index + delta + list.length) % list.length;
      this.hoverIndex = { scope, index: next };
    },
    /** Return true when given index/scope is the current active selection. */
    isActiveIndex(idx, scope) {
      return this.hoverIndex.scope === scope && this.hoverIndex.index === idx;
    },
    /** Select the currently active item (via keyboard Enter). */
    selectActive() {
      const scope = this.hoverIndex.scope;
      const list = scope === "recent" ? this.recentStores : this.filteredStores;
      const item = list[this.hoverIndex.index];
      if (item) this.selectStoreAndRemember(item);
    },
    /** Programmatically close the Bootstrap dropdown. */
    closeDropdown() {
      try {
        const btn = this.$refs.storeToggle;
        if (btn) btn.click();
      } catch(_) {}
    },
    /**
     * Select store via existing logic and remember it in a small recent list.
     * @param {{StoreName:string, StoreDB:(string|null)}} store
     */
    selectStoreAndRemember(store) {
      this.selectStore(store);
      this.pushRecent(store);
      this.closeDropdown();
    },
    /** Push a store to the recent list (localStorage‑backed). */
    pushRecent(store) {
      const key = store.StoreDB || store.StoreName;
      const existing = this.recentStores.filter(s => (s.StoreDB || s.StoreName) !== key);
      this.recentStores = [store, ...existing].slice(0, this.maxRecent);
      try { localStorage.setItem("recent_stores", JSON.stringify(this.recentStores)); } catch(_) {}
    },
    /**
     * Auto‑select the only available real store and skip the dropdown.
     * @param {Array<{StoreName:string, StoreDB:(string|null)}>} list
     */
    ensureSingleStoreAutoSelect(list) {
      try {
        const stores = Array.isArray(list) ? list : this.realStores;
        if (stores.length === 1) {
          const only = stores[0];
          const current = this.store;
          const curKey = current ? (current.StoreDB || current.StoreName) : null;
          const onlyKey = (only.StoreDB || only.StoreName);
          if (curKey !== onlyKey) {
            this.selectStore(only);
          }
        }
      } catch (_) {
        // no-op
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-menu.show {
  z-index: 9999;
}

.offline {
  color: var(--danger);
  font-size: 2rem;
}

.store {
  color: var(--nav-text) !important;
  font-size: clamp(1rem, 1.2vw, 2rem) !important;
}

.store-mobile {
  display: none !important;
}

@media only screen and (max-width: 700px) {
  .store {
    display: none;
  }
}

@media only screen and (max-width: 699px) {
  .store-mobile {
    display: block !important;
    padding: 5px;
    background-color: var(--card-bg);
    width: 100vw !important;
  }
}

.navigation-bar {
  position: sticky;
  top: 0;
  z-index: 1000;
  height: 70px;
  display: flex;
  justify-content: space-between !important;
  align-items: center;
  padding: 0 var(--space-4, 16px);
  background: var(--nav-surface);
  color: var(--nav-text);
  border-bottom: 1px solid var(--nav-divider);
  backdrop-filter: saturate(120%) blur(6px);
}
/* Improve visibility of all nav content without affecting select-doc button */
.navigation-bar :where(h1,h2,h3,h4,h5,h6,p,span,a,svg,button) {
  color: var(--nav-text) !important;
}
/* Keep the store picker readable on its own surface */
.navigation-bar .select-doc-btn {
  background-color: var(--card-bg);
  color: var(--text-primary) !important;
  border: 1px solid var(--grid-lines);
}

/* Store toggle integrated current store label (Option A) */
.navigation-bar .store-toggle-btn.store-toggle-ghost {
  background: transparent !important;
  color: var(--text-primary) !important;
  border: 1px solid transparent;
  border-radius: var(--radius-button) !important;
  min-height: 34px;
  padding: 4px 8px;
  line-height: 1.2;
  box-shadow: none;
}
.navigation-bar .store-toggle-btn.store-toggle-ghost:hover,
.navigation-bar .store-toggle-btn.store-toggle-ghost:focus {
  border-color: var(--grid-lines);
  background: color-mix(in srgb, var(--card-bg) 90%, transparent);
}
.navigation-bar .store-toggle-btn.store-toggle-ghost::after {
  border-top-color: var(--text-secondary);
  margin-left: 6px;
  transform: translateY(1px);
}
.store-toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 28ch;
}
.store-icon { color: var(--text-secondary); }
.store-toggle-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: var(--text-primary);
}
@media (max-width: 700px) {
  .navigation-bar .store-toggle-btn.store-toggle-ghost { min-height: 36px; padding: 6px 8px; }
  .store-toggle-row { max-width: 22ch; }
}

.navigation-location {
  display: flex;
  justify-content: end;
  align-items: end;
}

.select-doc-btn {
  border-radius: var(--radius-button);
  background-color: var(--card-bg);
  border: 1px solid var(--grid-lines);
  color: var(--text-primary);
}

.burger {
  width: 40px;
  height: 40px;
  background: transparent;
  color: var(--nav-text);
  font-size: 30px;
}

.user-settings {
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
}

.custom-tooltip-right {
  position: relative;
  display: inline-block;
  margin-left: 1rem;
}

.custom-tooltip-right .tooltip-arrow {
  position: absolute;
  top: 50%;
  left: 100%;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent var(--card-bg);
}

.custom-tooltip-right .tooltip-body {
  background: var(--card-bg);
  color: var(--text-primary);
  padding: 5px;
  border-radius: var(--radius-card);
  border: 1px solid var(--accent-primary);
  box-shadow: var(--shadow);
}

/* Store picker (searchable) */
.store-picker .dropdown-menu {
  z-index: 9999;
  min-width: 280px;
  background: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow);
}
.search-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.store-search {
  flex: 1;
  background: var(--bg);
  color: var(--text-primary);
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-input);
  padding: 6px 28px 6px 10px;
  font-size: 14px; /* 16px on iOS below */
}
.btn-clear {
  position: absolute;
  right: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  padding: 4px 6px;
  line-height: 1;
  border-radius: var(--radius-button);
}
.btn-clear:focus { outline: 2px solid var(--accent-secondary); }
.section-label {
  font-size: 12px;
  color: var(--text-secondary);
  padding: 4px 8px;
}
.divider { border-top: 1px solid var(--grid-lines); margin: 6px 0; }
.store-item { border-radius: var(--radius-card); }
.store-item.active,
.store-item:hover {
  background: color-mix(in srgb, var(--accent-primary) 12%, var(--card-bg));
  color: var(--text-primary);
}

/* Improve visibility of store labels and add 'Main' badge styles */
.store-item { color: var(--text-primary); font-weight: 500; }
.store-item .store-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.store-item .store-name { letter-spacing: 0.2px; }

/* Lighter outlined chip for Main store */
.badge-main, .chip-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--success);
  background: color-mix(in srgb, var(--success) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--success) 40%, transparent);
  border-radius: var(--radius-card);
}
.store-item.active .badge-main {
  background: color-mix(in srgb, var(--success) 10%, transparent);
  border-color: color-mix(in srgb, var(--success) 50%, transparent);
}

@media (max-width: 700px) {
  .store-picker .dropdown-menu {
    min-width: min(100vw - 16px, 420px);
  }
  .store-search { font-size: 16px; } /* avoid iOS zoom */
}
</style>


<style lang="scss" scoped>
.dropdown-menu.show {
  z-index: 9999;
}

.offline {
  color: var(--danger);
  font-size: 2rem;
}

.store {
  color: var(--nav-text) !important;
  font-size: clamp(1rem, 1.2vw, 2rem) !important;
}

.store-mobile {
  display: none !important;
}

@media only screen and (max-width: 700px) {
  .store {
    display: none;
  }
}

@media only screen and (max-width: 699px) {
  .store-mobile {
    display: block !important;
    padding: 5px;
    background-color: var(--card-bg);
    width: 100vw !important;
  }
}

.navigation-bar {
  position: sticky;
  top: 0;
  z-index: 1000;
  height: 70px;
  display: flex;
  justify-content: space-between !important;
  align-items: center;
  padding: 0 var(--space-4, 16px);
  background: var(--nav-surface);
  color: var(--nav-text);
  border-bottom: 1px solid var(--nav-divider);
  backdrop-filter: saturate(120%) blur(6px);
}
/* Improve visibility of all nav content without affecting select-doc button */
.navigation-bar :where(h1,h2,h3,h4,h5,h6,p,span,a,svg,button) {
  color: var(--nav-text) !important;
}
/* Keep the store picker readable on its own surface */
.navigation-bar .select-doc-btn {
  background-color: var(--card-bg);
  color: var(--text-primary) !important;
  border: 1px solid var(--grid-lines);
}

/* Store toggle integrated current store label (Option A) */
.navigation-bar .store-toggle-btn.store-toggle-ghost {
  background: transparent !important;
  color: var(--text-primary) !important;
  border: 1px solid transparent;
  border-radius: var(--radius-button) !important;
  min-height: 34px;
  padding: 4px 8px;
  line-height: 1.2;
  box-shadow: none;
}
.navigation-bar .store-toggle-btn.store-toggle-ghost:hover,
.navigation-bar .store-toggle-btn.store-toggle-ghost:focus {
  border-color: var(--grid-lines);
  background: color-mix(in srgb, var(--card-bg) 90%, transparent);
}
.navigation-bar .store-toggle-btn.store-toggle-ghost::after {
  border-top-color: var(--text-secondary);
  margin-left: 6px;
  transform: translateY(1px);
}
.store-toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 28ch;
}
.store-icon { color: var(--text-secondary); }
.store-toggle-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: var(--text-primary);
}
@media (max-width: 700px) {
  .navigation-bar .store-toggle-btn.store-toggle-ghost { min-height: 36px; padding: 6px 8px; }
  .store-toggle-row { max-width: 22ch; }
}

.navigation-location {
  display: flex;
  justify-content: end;
  align-items: end;
}

.select-doc-btn {
  border-radius: var(--radius-button);
  background-color: var(--card-bg);
  border: 1px solid var(--grid-lines);
  color: var(--text-primary);
}

.burger {
  width: 40px;
  height: 40px;
  background: transparent;
  color: var(--nav-text);
  font-size: 30px;
}

.user-settings {
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
}

.custom-tooltip-right {
  position: relative;
  display: inline-block;
  margin-left: 1rem;
}

.custom-tooltip-right .tooltip-arrow {
  position: absolute;
  top: 50%;
  left: 100%;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent var(--card-bg);
}

.custom-tooltip-right .tooltip-body {
  background: var(--card-bg);
  color: var(--text-primary);
  padding: 5px;
  border-radius: var(--radius-card);
  border: 1px solid var(--accent-primary);
  box-shadow: var(--shadow);
}

/* Store picker (searchable) */
.store-picker .dropdown-menu {
  z-index: 9999;
  min-width: 280px;
  background: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow);
}
.search-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.store-search {
  flex: 1;
  background: var(--bg);
  color: var(--text-primary);
  border: 1px solid var(--grid-lines);
  border-radius: var(--radius-input);
  padding: 6px 28px 6px 10px;
  font-size: 14px; /* 16px on iOS below */
}
.btn-clear {
  position: absolute;
  right: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  padding: 4px 6px;
  line-height: 1;
  border-radius: var(--radius-button);
}
.btn-clear:focus { outline: 2px solid var(--accent-secondary); }
.section-label {
  font-size: 12px;
  color: var(--text-secondary);
  padding: 4px 8px;
}
.divider { border-top: 1px solid var(--grid-lines); margin: 6px 0; }
.store-item { border-radius: var(--radius-card); }
.store-item.active,
.store-item:hover {
  background: color-mix(in srgb, var(--accent-primary) 12%, var(--card-bg));
  color: var(--text-primary);
}

/* Improve visibility of store labels and add 'Main' badge styles */
.store-item { color: var(--text-primary); font-weight: 500; }
.store-item .store-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.store-item .store-name { letter-spacing: 0.2px; }

/* Lighter outlined chip for Main store */
.badge-main, .chip-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--success);
  background: color-mix(in srgb, var(--success) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--success) 40%, transparent);
  border-radius: var(--radius-card);
}
.store-item.active .badge-main {
  background: color-mix(in srgb, var(--success) 10%, transparent);
  border-color: color-mix(in srgb, var(--success) 50%, transparent);
}

@media (max-width: 700px) {
  .store-picker .dropdown-menu {
    min-width: min(100vw - 16px, 420px);
  }
  .store-search { font-size: 16px; } /* avoid iOS zoom */
}

/* THEME: Kebab menu (mobile quick actions) styling using tokens (<=5px radius) */
.navigation-bar .dropdown:not(.store-picker) .dropdown-menu {
  background: var(--card-bg) !important;
  color: var(--text-primary) !important;
  border: 1px solid var(--grid-lines) !important;
  border-radius: var(--radius-card) !important; /* <=5px cap */
  box-shadow: var(--shadow, 0 8px 24px rgba(0,0,0,0.3));
  min-width: 200px;
}
.navigation-bar .dropdown:not(.store-picker) .dropdown-item,
.navigation-bar .dropdown:not(.store-picker) .dropdown-item-text {
  color: var(--text-primary) !important;
  border-radius: var(--radius-input) !important;
}
.navigation-bar .dropdown:not(.store-picker) .dropdown-item:hover,
.navigation-bar .dropdown:not(.store-picker) .dropdown-item:focus {
  background: color-mix(in srgb, var(--accent-primary) 10%, var(--card-bg)) !important;
  color: var(--text-primary) !important;
  outline: none;
}
.navigation-bar .dropdown:not(.store-picker) .dropdown-divider {
  border-top: 1px solid var(--grid-lines) !important;
  opacity: 1;
}
/* Focus ring for accessibility */
.navigation-bar .dropdown:not(.store-picker) .dropdown-item:focus-visible {
  outline: 2px solid var(--accent-secondary);
  outline-offset: 1px;
}
</style>


<style scoped>
/* Z-index fixes for mobile dropdowns (kebab + store picker)
   Ensure nav menus render above the timeline using tokenized layers */
.navigation-bar .dropdown-menu,
.navigation-bar .dropdown-menu.show,
.navigation-bar .store-picker .dropdown-menu {
  z-index: var(--z-dropdown) !important;
}

/* Prevent clipping by parents on small screens */
.navigation-bar .dropdown,
.navigation-bar .store-picker {
  position: relative;
  overflow: visible;
}

/* Force the Store Picker to open downward, left-aligned under the toggle */
.navigation-bar .store-picker .dropdown-menu {
  top: 100% !important;
  left: 0 !important;
  right: auto !important;
  margin-top: 6px !important;
}
</style>
